import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, CheckCircle } from 'lucide-react';

export default function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(email, password, rememberMe);
        if (result.success) {
          let dest = '/home';
          try {
            const last = localStorage.getItem('clab.lastPath');
            if (last && last !== '/' && last !== '/login') dest = last;
            if (sessionStorage.getItem('clab.archivio')) dest = '/archivio';
          } catch (_) {}
          navigate(dest);
        } else {
          setError(result.error);
        }
      } else {
        result = await register(email, password, name);
        if (result.success) {
          // Show success message instead of navigating
          setRegistrationSuccess(true);
        } else {
          setError(result.error);
        }
      }
    } catch (err) {
      setError('Si è verificato un errore. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  // Registration success screen
  if (registrationSuccess) {
    return (
      <div className="min-h-svh flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 py-6 px-2">
        <div className="w-full max-w-md px-4 sm:px-6">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6 md:p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Registrazione completata!</h2>
            <p className="text-gray-600 mb-6">
              Il tuo account è stato creato. Un amministratore dovrà abilitare il tuo accesso prima che tu possa accedere alla piattaforma.
            </p>
            <Button
              onClick={() => {
                setRegistrationSuccess(false);
                setIsLogin(true);
                setEmail('');
                setPassword('');
                setName('');
              }}
              className="w-full font-bold border border-gray-900 text-gray-900 bg-transparent hover:bg-gray-100 h-11 rounded-xl"
            >
              Torna al login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-svh overflow-hidden flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 px-2 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))]">
      <div className="w-full max-w-md px-4 sm:px-6">
        <div className="text-center mb-4 md:mb-8">
          <div className="flex items-center justify-center">
            <img
              src="/logo-clab.svg?v=6"
              alt="C-LAB"
              className="w-24 h-24 md:w-28 md:h-28 rounded-2xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-5 md:p-8">
          <p className="text-base font-semibold text-gray-900 mb-4 md:mb-6">
            {isLogin ? 'Accedi al tuo account' : 'Crea un nuovo account'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="text-sm font-semibold mb-2 block text-gray-700">
                  Nome
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required={!isLogin}
                  data-testid="name-input"
                  className="rounded-xl border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all duration-200"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-sm font-semibold mb-2 block text-gray-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
                className="rounded-xl border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all duration-200"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-semibold mb-2 block text-gray-700">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
                className="rounded-xl border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-gray-900 transition-all duration-200"
              />
            </div>

            {isLogin && (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rememberMe"
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  data-testid="remember-me-checkbox"
                  className="border-gray-300 data-[state=checked]:bg-gray-900 data-[state=checked]:border-gray-900"
                />
                <Label
                  htmlFor="rememberMe"
                  className="text-sm font-medium text-gray-600 cursor-pointer whitespace-nowrap"
                >
                  Ricordami su questo dispositivo
                </Label>
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl" data-testid="error-message">
                {error === 'Something went wrong. Please try again.' ? 'Credenziali non valide. Riprova.' : error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="submit-btn"
              className="w-full font-bold border border-gray-900 text-gray-900 bg-transparent hover:bg-gray-100 transition-colors duration-200 h-11 rounded-xl"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Caricamento...
                </>
              ) : isLogin ? (
                'Accedi'
              ) : (
                'Registrati'
              )}
            </Button>
          </form>

          <div className="mt-4 md:mt-6 text-center">
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              data-testid="toggle-auth-mode-btn"
              className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors duration-200"
            >
              {isLogin ? 'Non hai un account? Registrati' : 'Hai già un account? Accedi'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}